/**
 * The content-driven public pages.
 *
 * `CmsPage` renders whatever sections the page carries, so home, services, about,
 * contact and quote are all the same component with a different slug. The pages that
 * need something a section cannot express — a case study's body, a filterable project
 * index — have their own component here.
 */

import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { RenderSection, RenderSections } from '../../sections/index.jsx';
import { Markdown, plainText } from '../../lib/markdown.jsx';
import { EmptyState, Pagination, Skeleton } from '../../components/ui.jsx';
import { CoverArtwork } from '../../components/Artwork.jsx';
import { Breadcrumbs, HOME_CRUMB, type Crumb } from '../../components/Breadcrumbs.jsx';
import {
  articleLd,
  breadcrumbLd,
  itemListLd,
  organizationLd,
  serviceLd,
  useSeo,
  websiteLd,
  SITE_URL,
  type SeoInput,
} from '../../lib/seo.js';
import { useBootstrap, usePage, usePost, usePosts, useProject, useProjects, useService, useServices, setting } from '../../lib/queries.js';

/* -------------------------------------------------------------------------- */
/* Document head                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The document head for one page, and the structured data under it.
 *
 * Every public screen calls this. It reads the brand and SEO settings so the title
 * template, the default description and the indexing switch are all editable in the
 * back office, then hands the rest to `applySeo`.
 *
 * The organisation and website nodes are attached to EVERY page rather than only the
 * home page. Search engines read one URL at a time and may never fetch the home page
 * first; a company described only there is a company most crawls never see.
 */
export function usePageSeo(input: SeoInput & { crumbs?: Crumb[] }) {
  const { data } = useBootstrap();

  const siteName = setting(data, 'brand.company_name', 'Serendib IT Solutions');
  const logo = setting(data, 'brand.logo_url', '/brand/mark.png');

  const socials = [
    setting(data, 'social.facebook', ''),
    setting(data, 'social.instagram', ''),
    setting(data, 'social.linkedin', ''),
    setting(data, 'social.x', ''),
  ].filter(Boolean);

  const graph = [
    organizationLd({
      name: siteName,
      url: SITE_URL,
      logo,
      email: setting(data, 'contact.email', '') || null,
      phone: setting(data, 'contact.phone', '') || null,
      streetAddress: setting(data, 'contact.address_line1', '') || null,
      addressLocality: setting(data, 'contact.address_line2', '') || null,
      socials,
    }),
    websiteLd(SITE_URL, siteName),
    ...(input.crumbs && input.crumbs.length > 1 ? [breadcrumbLd(input.crumbs)] : []),
    ...(input.jsonLd ?? []),
  ];

  useSeo(
    { ...input, jsonLd: graph },
    {
      siteName,
      titleTemplate: setting(data, 'seo.title_template', '%s · Serendib IT Solutions'),
      // Matches the seeded default in `services/settings.ts`. A settings request that
      // fails must not silently downgrade every page's title to the bare brand name.
      defaultTitle: setting(data, 'seo.default_title', 'Serendib IT Solutions — software, hardware, installed'),
      defaultDescription: setting(data, 'seo.default_description', ''),
      defaultImage: setting(data, 'seo.default_og_image', '') || logo,
      allowIndexing: setting<boolean>(data, 'seo.allow_indexing', true) !== false,
    },
  );
}

/** Kept for the screens that only need a title and a description. */
function useDocumentMeta(title?: string | null, description?: string | null) {
  usePageSeo({ title, description });
}

/* -------------------------------------------------------------------------- */
/* Loading and failure                                                         */
/* -------------------------------------------------------------------------- */

/** Skeletons shaped like the panels they replace, so nothing reflows on arrival. */
function PageSkeleton() {
  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <Skeleton height={420} radius={34} />
      <Skeleton height={220} radius={34} />
      <Skeleton height={320} radius={34} />
    </div>
  );
}

export function NotFound({ what = 'page' }: { what?: string }) {
  usePageSeo({ title: 'Not found', noIndex: true });
  return (
    <div className="wrap section">
      <div className="panel">
        <EmptyState
          // The only heading on this page, so it is the page's h1.
          headingLevel="h1"
          title={`This ${what} does not exist`}
          reason="It may have been moved or deleted. Please also check the address for a spelling mistake."
          action={<Link className="btn btn--primary" to="/">Back to the home page</Link>}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* CMS page                                                                    */
/* -------------------------------------------------------------------------- */

export function CmsPage({ slug }: { slug: string }) {
  const { data, isLoading, isError, error } = usePage(slug);
  const isHome = slug === 'home';

  usePageSeo({
    /**
     * A page's `title` is its name in the back office and in the navigation. For every
     * page but one that is also the right search title. The home page's is "Home", and
     * "Home · Serendib IT Solutions" spends the most valuable words in a search result
     * — the ones a reader actually scans — saying nothing. So the home page falls back
     * to the site's own default title, which is written to be read cold. An explicit
     * SEO title set in the back office still wins, on every page including this one.
     */
    title: data?.page.seoTitle ?? (isHome ? null : data?.page.title),
    description: data?.page.seoDescription,
    image: data?.page.ogImageUrl,
    // The home page's canonical is the bare origin, not `/home` — the slug is an
    // implementation detail of the page store and must never reach a search result.
    canonicalPath: isHome ? '/' : `/${slug}`,
    crumbs: isHome ? [] : [HOME_CRUMB, { name: data?.page.title ?? '', path: `/${slug}` }],
  });

  if (isLoading) return <PageSkeleton />;
  if (isError && (error as { status?: number })?.status === 404) return <NotFound />;
  if (isError || !data) {
    return (
      <div className="wrap section">
        <div className="panel">
          <EmptyState title="This page could not be loaded" reason="The connection to our server failed. Please try again." action={<button className="btn btn--secondary" onClick={() => location.reload()}>Retry</button>} />
        </div>
      </div>
    );
  }

  if (data.sections.length === 0) {
    return (
      <div className="wrap section">
        <div className="panel">
          <EmptyState title={data.page.title} reason="This page has no content yet." />
        </div>
      </div>
    );
  }

  return <RenderSections sections={data.sections} />;
}

/* -------------------------------------------------------------------------- */
/* Projects                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The project index.
 *
 * This is where the rest of the portfolio lives. The home page carries exactly ONE
 * project — the featured one — and everything else is here, filterable by service.
 * That split is the decision recorded in TECHNICAL_DOCUMENT.md §2.
 */
export function ProjectsIndex() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? 1);
  const service = params.get('service') ?? undefined;

  const { data, isLoading } = useProjects({ page, pageSize: 9, service });
  const { data: services } = useServices();

  /**
   * The heading comes from the back office, like every other page's.
   *
   * This page is assembled here rather than from sections, because the filter, the
   * grid and the paging are not things a section can express. The consequence was that
   * the `projects` page in the back office had a hero section on it that rendered
   * NOWHERE: an editor could rewrite the heading, save it, reload the site and see the
   * old words still there, with nothing to explain why. Reading just the hero closes
   * that gap — the text is editable, and the parts that cannot be sections stay here.
   *
   * The literals below remain as the fallback, so the page still reads correctly
   * against a database that has no hero for it.
   */
  const { data: pageData } = usePage('projects');
  const hero = (pageData?.sections ?? []).find((s) => s.type === 'hero' && s.isEnabled !== false)?.payload as
    | { eyebrow?: string; heading?: string; body?: string }
    | undefined;
  usePageSeo({
    title: 'Our work',
    description:
      'Work by Serendib IT Solutions — counter systems, custom software and hardware fitted in shops across Sri Lanka.',
    // A filtered or paged view canonicalises to the bare index. Otherwise every
    // combination of filter and page competes with it as a near-duplicate.
    canonicalPath: '/projects',
    noIndex: Boolean(service) || page > 1,
    crumbs: [HOME_CRUMB, { name: 'Our work', path: '/projects' }],
    jsonLd: data?.items.length
      ? [itemListLd(data.items.map((pr) => ({ name: pr.title, path: `/projects/${pr.slug}` })))]
      : [],
  });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    // Changing a filter always returns to page one. Staying on page 4 of a filter that
    // now has two pages shows an empty grid and looks like there is nothing there.
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  return (
    <>
    <div className="wrap section stack" style={{ gap: 20 }}>
      <div className="panel">
        <p className="eyebrow">{hero?.eyebrow ?? 'Our work'}</p>
        <h1 style={{ marginTop: 12 }}>{hero?.heading ?? 'Systems we built, and what they had to handle.'}</h1>
        <p className="lead measure" style={{ marginTop: 16 }}>
          {hero?.body ?? 'Every system here is in daily use. Where we give a number, we measured it.'}
        </p>

        {services && services.length > 0 ? (
          <div className="row" style={{ marginTop: 26, gap: 8 }}>
            <button className={`btn btn--sm ${!service ? 'btn--primary' : 'btn--secondary'}`} onClick={() => setParam('service', null)}>
              All work
            </button>
            {services.map((s) => (
              <button
                key={s.slug}
                className={`btn btn--sm ${service === s.slug ? 'btn--primary' : 'btn--secondary'}`}
                onClick={() => setParam('service', s.slug)}
              >
                {s.title}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="panel">
        {isLoading ? (
          <div className="grid grid--3 grid--cards">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={300} radius={22} />)}</div>
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No projects to show"
            reason={service ? 'We have not published any work using that service yet.' : 'We have not published any work yet.'}
            action={service ? <button className="btn btn--secondary" onClick={() => setParam('service', null)}>Show all work</button> : undefined}
          />
        ) : (
          <>
            <div className="grid grid--3 grid--cards">
              {data.items.map((project) => (
                <Link key={project.id} to={`/projects/${project.slug}`} className="card" style={{ color: 'var(--ink)', background: 'var(--panel-2)' }}>
                  <div style={{ aspectRatio: '16 / 10', background: 'var(--wash)' }}>
                    {project.coverImageUrl ? (
                      <img src={project.coverImageUrl} alt={project.coverImageAlt ?? ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <CoverArtwork seed={project.slug} label={project.title} />
                    )}
                  </div>
                  <div className="stack" style={{ gap: 8, padding: 22 }}>
                    <div className="row" style={{ gap: 8 }}>
                      {project.client ? <p className="eyebrow">{project.client}</p> : null}
                      {project.year ? <p className="xs dim">· {project.year}</p> : null}
                    </div>
                    <h3>{project.title}</h3>
                    <p className="small muted">{project.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPage={(n) => setParam('page', String(n))}
              filtered={Boolean(service)}
            />
          </>
        )}
      </div>

    </div>

    {/*
      Testimonials, under the work they are about.

      Rendered explicitly for the same reason the hero is read explicitly above: this
      page is assembled by hand, so a section added in the back office would otherwise
      sit in the editor and appear nowhere. Driving it from the section row still means
      the heading, the limit and the on/off switch stay editable — and disabling the
      section in the back office removes it here, as an editor would expect.

      It sits OUTSIDE the wrap above, as a sibling, because `Panel` brings its own
      `.section.wrap`. Nested inside, the 20px `padding-inline` applied twice and the
      panel came out 1320px wide against every other panel's 1360 — inset by 20px on
      each side, which is exactly the kind of ragged edge this page must not have.

      Hidden while a filter is active: the quotes are about the practice as a whole,
      not about whichever service you just narrowed to, and leaving them under a
      filtered grid reads as though they belong to it.
    */}
    {!service ? (() => {
      const section = (pageData?.sections ?? []).find((s) => s.type === 'testimonials' && s.isEnabled !== false);
      return section ? <RenderSection section={section} /> : null;
    })() : null}
    </>
  );
}

export function ProjectDetail() {
  const { slug = '' } = useParams();
  const { data, isLoading, isError, error } = useProject(slug);

  const project = data?.project as Record<string, any> | undefined;
  usePageSeo({
    title: project?.seoTitle ?? project?.title,
    description: project?.seoDescription ?? plainText(project?.summary),
    image: project?.ogImageUrl ?? project?.coverImageUrl,
    type: 'article',
    canonicalPath: `/projects/${slug}`,
    crumbs: [HOME_CRUMB, { name: 'Our work', path: '/projects' }, { name: project?.title ?? '', path: `/projects/${slug}` }],
    jsonLd: project
      ? [
          articleLd({
            headline: project.title,
            description: plainText(project.summary),
            image: project.coverImageUrl,
            path: `/projects/${slug}`,
            published: project.createdAt,
            modified: project.updatedAt,
          }),
        ]
      : [],
  });

  if (isLoading) return <PageSkeleton />;
  if (isError && (error as { status?: number })?.status === 404) return <NotFound what="project" />;
  if (!project) return <NotFound what="project" />;

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <Breadcrumbs
        trail={[HOME_CRUMB, { name: 'Our work', path: '/projects' }, { name: project.title, path: `/projects/${slug}` }]}
      />

      <div className="panel">

        {project.client ? <p className="eyebrow">{project.client}{project.year ? ` · ${project.year}` : ''}</p> : null}
        <h1 style={{ marginTop: 12 }}>{project.title}</h1>
        <p className="lead measure" style={{ marginTop: 16 }}>{project.summary}</p>

        {project.technologies?.length ? (
          <div className="row" style={{ marginTop: 22, gap: 8 }}>
            {project.technologies.map((tech: string) => <span key={tech} className="pill">{tech}</span>)}
          </div>
        ) : null}
      </div>

      <div className="panel panel--flush">
        {project.coverImageUrl ? (
          <img src={project.coverImageUrl} alt={project.coverImageAlt ?? ''} style={{ width: '100%', display: 'block' }} />
        ) : (
          <div style={{ aspectRatio: '16 / 6' }}>
            <CoverArtwork seed={project.slug} label={project.title} />
          </div>
        )}
      </div>

      {project.outcomes?.length ? (
        <div className="panel">
          <h2 style={{ fontSize: 'var(--t-h3)' }}>What it achieved</h2>
          <ul className="stack" style={{ gap: 11, marginTop: 18 }}>
            {project.outcomes.map((outcome: string) => (
              <li key={outcome} className="row" style={{ gap: 12, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <span aria-hidden="true" style={{ color: 'var(--accent-deep)', fontWeight: 700 }}>✓</span>
                <span className="muted">{outcome}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {project.body ? (
        <div className="panel"><Markdown>{project.body}</Markdown></div>
      ) : null}

      {data?.related?.length ? (
        <div className="panel">
          <h2 style={{ fontSize: 'var(--t-h3)' }}>More of our work</h2>
          <div className="grid grid--3 grid--cards" style={{ marginTop: 22 }}>
            {data.related.map((related) => (
              <Link key={related.id} to={`/projects/${related.slug}`} className="card" style={{ color: 'var(--ink)', background: 'var(--panel-2)', padding: 20 }}>
                {related.client ? <p className="eyebrow">{related.client}</p> : null}
                <h3 style={{ marginTop: 6, fontSize: 16 }}>{related.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <div className="panel spread" style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}>
        <div>
          <h2 style={{ fontSize: 'var(--t-h3)' }}>Something similar in mind?</h2>
          <p className="small" style={{ marginTop: 6, opacity: 0.85 }}>Tell us the shape of it and we will come back with questions.</p>
        </div>
        {/* `btn--on-accent`, not `btn--secondary`. Secondary is `--wash`, a 6% dark
            tint meant for a light panel — on the accent green it all but vanishes. */}
        <Link className="btn btn--on-accent" to="/contact">Start a conversation</Link>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Services                                                                    */
/* -------------------------------------------------------------------------- */

export function ServiceDetail() {
  const { slug = '' } = useParams();
  const { data, isLoading, isError, error } = useService(slug);

  const service = data?.service as Record<string, any> | undefined;
  usePageSeo({
    title: service?.seoTitle ?? service?.title,
    description: service?.seoDescription ?? service?.summary,
    image: service?.ogImageUrl ?? service?.imageUrl,
    canonicalPath: `/services/${slug}`,
    crumbs: [HOME_CRUMB, { name: 'Services', path: '/services' }, { name: service?.title ?? '', path: `/services/${slug}` }],
    jsonLd: service ? [serviceLd({ name: service.title, description: service.summary, path: `/services/${slug}` })] : [],
  });

  if (isLoading) return <PageSkeleton />;
  if (isError && (error as { status?: number })?.status === 404) return <NotFound what="service" />;
  if (!service) return <NotFound what="service" />;

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <Breadcrumbs
        trail={[HOME_CRUMB, { name: 'Services', path: '/services' }, { name: service.title, path: `/services/${slug}` }]}
      />

      <div className="panel">
        <h1>{service.title}</h1>
        <p className="lead measure" style={{ marginTop: 16 }}>{service.summary}</p>

        {service.bullets?.length ? (
          <ul className="grid grid--2" style={{ marginTop: 26, gap: 12 }}>
            {service.bullets.map((bullet: string) => (
              <li key={bullet} className="row" style={{ gap: 10, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <span aria-hidden="true" style={{ color: 'var(--accent-deep)', fontWeight: 700 }}>✓</span>
                <span className="small muted">{bullet}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {service.body ? <div className="panel"><Markdown>{service.body}</Markdown></div> : null}

      {data?.projects?.length ? (
        <div className="panel">
          <h2 style={{ fontSize: 'var(--t-h3)' }}>Work built on this</h2>
          <div className="grid grid--3 grid--cards" style={{ marginTop: 22 }}>
            {data.projects.map((project) => (
              <Link key={project.id} to={`/projects/${project.slug}`} className="card" style={{ color: 'var(--ink)', background: 'var(--panel-2)', padding: 20 }}>
                {project.client ? <p className="eyebrow">{project.client}</p> : null}
                <h3 style={{ marginTop: 6, fontSize: 16 }}>{project.title}</h3>
                <p className="small muted" style={{ marginTop: 8 }}>{project.summary.slice(0, 110)}…</p>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <div className="panel spread" style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}>
        <h2 style={{ fontSize: 'var(--t-h3)' }}>Talk to us about {service.title.toLowerCase()}</h2>
        <Link className="btn btn--on-accent" to="/quote">Request a quote</Link>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* News                                                                        */
/* -------------------------------------------------------------------------- */

export function BlogIndex() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? 1);
  const { data, isLoading } = usePosts({ page, pageSize: 9 });
  usePageSeo({
    title: 'News',
    description: 'Notes on point of sale, custom software and running IT systems in Sri Lanka.',
    canonicalPath: '/blog',
    noIndex: page > 1,
    crumbs: [HOME_CRUMB, { name: 'News', path: '/blog' }],
    jsonLd: data?.items.length
      ? [itemListLd(data.items.map((post) => ({ name: post.title, path: `/blog/${post.slug}` })))]
      : [],
  });

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <div className="panel">
        <p className="eyebrow">News</p>
        <h1 style={{ marginTop: 12 }}>News from our workshop</h1>
        <p className="lead measure" style={{ marginTop: 16 }}>
          What we learn building and fitting systems that must keep working when the internet does not.
        </p>
      </div>

      <div className="panel">
        {isLoading ? (
          <div className="grid grid--3 grid--cards">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={260} radius={22} />)}</div>
        ) : !data || data.items.length === 0 ? (
          <EmptyState title="Nothing published yet" reason="The first article is on its way." />
        ) : (
          <>
            <div className="grid grid--3 grid--cards">
              {data.items.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="card" style={{ color: 'var(--ink)', background: 'var(--panel-2)' }}>
                  <div style={{ aspectRatio: '16 / 9' }}>
                    {post.coverImageUrl ? (
                      <img src={post.coverImageUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <CoverArtwork seed={post.slug} label={post.title} />
                    )}
                  </div>
                  <div className="stack" style={{ gap: 8, padding: 22 }}>
                    <div className="row" style={{ gap: 8 }}>
                      {post.category ? <span className="eyebrow">{post.category}</span> : null}
                      {post.publishedAt ? (
                        <time className="xs dim" dateTime={post.publishedAt}>
                          {new Date(post.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </time>
                      ) : null}
                    </div>
                    <h3>{post.title}</h3>
                    <p className="small muted">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={(n) => { const next = new URLSearchParams(params); next.set('page', String(n)); setParams(next); }} />
          </>
        )}
      </div>
    </div>
  );
}

export function PostDetail() {
  const { slug = '' } = useParams();
  const { data, isLoading, isError, error } = usePost(slug);
  const post = data?.post as Record<string, any> | undefined;
  usePageSeo({
    title: post?.seoTitle ?? post?.title,
    description: post?.seoDescription ?? post?.excerpt,
    image: post?.ogImageUrl ?? post?.coverImageUrl,
    type: 'article',
    canonicalPath: `/blog/${slug}`,
    publishedTime: post?.publishedAt,
    modifiedTime: post?.updatedAt,
    crumbs: [HOME_CRUMB, { name: 'News', path: '/blog' }, { name: post?.title ?? '', path: `/blog/${slug}` }],
    jsonLd: post
      ? [
          articleLd({
            headline: post.title,
            description: post.excerpt,
            image: post.coverImageUrl,
            path: `/blog/${slug}`,
            published: post.publishedAt,
            modified: post.updatedAt,
            author: post.authorName,
          }),
        ]
      : [],
  });

  if (isLoading) return <PageSkeleton />;
  if (isError && (error as { status?: number })?.status === 404) return <NotFound what="article" />;
  if (!post) return <NotFound what="article" />;

  return (
    <div className="wrap section stack" style={{ gap: 20 }}>
      <Breadcrumbs
        trail={[HOME_CRUMB, { name: 'News', path: '/blog' }, { name: post.title, path: `/blog/${slug}` }]}
      />

      <article className="panel">
        <div className="row" style={{ gap: 10 }}>
          {post.category ? <span className="pill pill--accent">{post.category}</span> : null}
          {post.publishedAt ? (
            <time className="xs dim" dateTime={post.publishedAt}>
              {new Date(post.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </time>
          ) : null}
          {post.authorName ? <span className="xs dim">· {post.authorName}</span> : null}
        </div>

        <h1 style={{ marginTop: 14 }}>{post.title}</h1>
        <p className="lead measure" style={{ marginTop: 16 }}>{post.excerpt}</p>

        {post.coverImageUrl ? (
          <img src={post.coverImageUrl} alt="" style={{ width: '100%', borderRadius: 'var(--r-card)', marginTop: 26 }} />
        ) : null}

        <div style={{ marginTop: 30 }}><Markdown>{post.body}</Markdown></div>
      </article>

      {data?.related?.length ? (
        <div className="panel">
          <h2 style={{ fontSize: 'var(--t-h3)' }}>Read next</h2>
          <div className="grid grid--2 grid--cards" style={{ marginTop: 20 }}>
            {data.related.map((related) => (
              <Link key={related.id} to={`/blog/${related.slug}`} className="card" style={{ color: 'var(--ink)', background: 'var(--panel-2)', padding: 20 }}>
                <h3 style={{ fontSize: 16 }}>{related.title}</h3>
                <p className="small muted" style={{ marginTop: 8 }}>{related.excerpt.slice(0, 110)}…</p>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { useDocumentMeta };
